import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, ProfileType } from '@/types'

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(b64))
  } catch {
    return null
  }
}

function deriveIsAdmin(user: User | null, accessToken: string | null): boolean {
  // Check stored user roles first
  if (user?.roles?.some((r) => r.toLowerCase() === 'admin')) return true
  // Fall back to JWT payload (authoritative — handles stale persisted state)
  if (accessToken) {
    const payload = decodeJwtPayload(accessToken)
    if (payload?.is_admin === true) return true
    if ((payload?.roles as string[] | undefined)?.some((r) => r.toLowerCase() === 'admin')) return true
  }
  return false
}

/**
 * Accesso al pannello CRM & Marketing: ruolo `sales`, oppure amministratore.
 *
 * L'amministratore è incluso di proposito. Un pannello che l'admin non vede diventa un
 * angolo cieco: chi deve verificare cosa fa la squadra commerciale dovrebbe farsi assegnare
 * un secondo ruolo per guardarci dentro, e non lo farebbe.
 *
 * Questo governa solo cosa si VEDE. Il permesso vero è nei servizi (crm, marketing, …):
 * una derivazione lato client è un suggerimento all'interfaccia, non una difesa — chiunque
 * può modificare ciò che gira nel proprio browser.
 */
function deriveIsSales(user: User | null, accessToken: string | null): boolean {
  if (deriveIsAdmin(user, accessToken)) return true
  if (user?.roles?.some((r) => r.toLowerCase() === 'sales')) return true
  if (accessToken) {
    const payload = decodeJwtPayload(accessToken)
    if ((payload?.roles as string[] | undefined)?.some((r) => r.toLowerCase() === 'sales')) return true
  }
  return false
}

interface AuthState {
  user: User | null
  accessToken: string | null
  profileType: ProfileType | null
  capabilities: string[]
  isAuthenticated: boolean
  isAdminUser: boolean               // reactive derived boolean — subscribe directly
  isSalesUser: boolean               // ruolo `sales` OPPURE admin — pannello CRM & Marketing
  impersonationToken: string | null
  impersonationUserId: string | null

  setAuth: (user: User, accessToken: string, refreshToken?: string) => void
  setProfileType: (profileType: ProfileType) => void
  setCapabilities: (caps: string[]) => void
  hasCapability: (cap: string) => boolean
  logout: () => void
  isAdmin: () => boolean             // kept for backwards compat
  hasProfile: (type: ProfileType) => boolean
  startImpersonation: (token: string, userId: string) => void
  stopImpersonation: () => void
  isImpersonating: () => boolean
  effectiveToken: () => string | null
  /** ID dell'utente "attivo": l'impersonato se in impersonificazione, altrimenti l'utente loggato. */
  effectiveUserId: () => string | null
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      profileType: null,
      capabilities: [],
      isAuthenticated: false,
      isAdminUser: false,
      isSalesUser: false,
      impersonationToken: null,
      impersonationUserId: null,

      setAuth: (user, accessToken, refreshToken) => {
        localStorage.setItem('accessToken', accessToken)
        if (refreshToken) localStorage.setItem('refreshToken', refreshToken)
        set({
          user, accessToken, isAuthenticated: true,
          isAdminUser: deriveIsAdmin(user, accessToken),
          isSalesUser: deriveIsSales(user, accessToken),
        })
      },

      setProfileType: (profileType) => set({ profileType }),

      setCapabilities: (caps) => set({ capabilities: [...new Set(caps)] }),

      hasCapability: (cap) => get().capabilities.includes(cap),

      logout: () => {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        sessionStorage.removeItem('impersonationToken')
        set({
          user: null, accessToken: null, profileType: null, capabilities: [],
          isAuthenticated: false, isAdminUser: false, isSalesUser: false,
          impersonationToken: null, impersonationUserId: null,
        })
      },

      // Function form kept for backwards compat — prefer isAdminUser for reactivity
      isAdmin: () => deriveIsAdmin(get().user, get().accessToken),

      hasProfile: (type: ProfileType) => get().profileType === type,

      startImpersonation: (token, userId) => {
        sessionStorage.setItem('impersonationToken', token)
        set({ impersonationToken: token, impersonationUserId: userId })
      },

      stopImpersonation: () => {
        sessionStorage.removeItem('impersonationToken')
        set({ impersonationToken: null, impersonationUserId: null })
      },

      isImpersonating: () => get().impersonationToken !== null,

      effectiveToken: () => {
        const { impersonationToken, accessToken } = get()
        return impersonationToken ?? accessToken
      },

      effectiveUserId: () => {
        const { impersonationUserId, user } = get()
        return impersonationUserId ?? user?.id ?? null
      },
    }),
    {
      name: 'edive-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        profileType: state.profileType,
        capabilities: state.capabilities,
        isAuthenticated: state.isAuthenticated,
        isAdminUser: state.isAdminUser,
        isSalesUser: state.isSalesUser,
      }),
      // Ricalcolo dopo il ripristino da localStorage: il valore salvato può essere vecchio
      // (ruolo revocato nel frattempo), il token no.
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isAdminUser = deriveIsAdmin(state.user, state.accessToken)
          state.isSalesUser = deriveIsSales(state.user, state.accessToken)
        }
      },
    }
  )
)
